import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { navigate } from "gatsby"
import {
  AUTH_TOKEN_KEY,
  createGuestFingerprint,
  createGuestToken,
  DEFAULT_GUEST_USAGE,
  GUEST_FINGERPRINT_KEY,
  GUEST_TOKEN_KEY,
  readGuestUsage,
  SaaSUser,
  UsageSummary,
  writeGuestUsage,
} from "../lib/saas"
import { identifyUser, resetAnalytics } from "../utils/analytics"

// Persists the post-auth redirect across signup → email-verify → login sessions
const PENDING_REDIRECT_KEY = "digitup_pending_redirect"

type AuthMode = "login" | "signup" | "forgot_password"

type AuthPayload = {
  token: string
  user: SaaSUser
  usage: UsageSummary
}

type ConsumeUsagePayload = {
  requestedCount: number
  toolId?: string
}

type ConsumeUsageResult = {
  grantedCount: number
  usage: UsageSummary
  requiresAuth: boolean
  reachedLimit: boolean
}

type SaaSContextValue = {
  authToken: string | null
  guestToken: string
  isAuthenticated: boolean
  isBootstrapping: boolean
  authLoading: boolean
  user: SaaSUser | null
  usage: UsageSummary
  usages: Record<string, UsageSummary>
  authModalOpen: boolean
  authMode: AuthMode
  openAuthModal: (mode?: AuthMode, redirectPath?: string) => void
  closeAuthModal: () => void
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, fullName?: string, mobile?: string) => Promise<any>
  loginWithGoogle: (firebaseIdToken: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  logout: () => void
  refreshUsage: (toolId?: string) => Promise<UsageSummary>
  consumeUsage: (payload: ConsumeUsagePayload) => Promise<ConsumeUsageResult>
  startUpgrade: () => Promise<void>
  requestCorporateVerificationCode: (email: string) => Promise<any>
  verifyCorporateEmailCode: (email: string, code: string) => Promise<void>
}

const SaaSContext = createContext<SaaSContextValue | null>(null)

const parseErrorMessage = async (response: Response) => {
  try {
    const data = await response.json()
    return data?.message || "Something went wrong."
  } catch {
    return "Something went wrong."
  }
}

const apiRequest = async <T,>(
  url: string,
  options: RequestInit = {},
  token?: string | null
): Promise<T> => {
  const headers = new Headers(options.headers || {})
  headers.set("Content-Type", "application/json")

  if (token) {
    headers.set("Authorization", `Bearer ${token}`)
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response))
  }

  return response.json()
}

const consumeGuestUsageLocally = (
  requestedCount: number,
  toolId = "url-extractor"
): ConsumeUsageResult => {
  const currentUsage = readGuestUsage(toolId)
  const grantedCount = Math.max(
    Math.min(requestedCount, currentUsage.remainingToday),
    0
  )

  const usage: UsageSummary = {
    ...currentUsage,
    usedToday: currentUsage.usedToday + grantedCount,
    remainingToday: Math.max(currentUsage.remainingToday - grantedCount, 0),
    totalReports: currentUsage.totalReports + (grantedCount > 0 ? 1 : 0),
  }

  writeGuestUsage(toolId, usage)

  return {
    grantedCount,
    usage,
    requiresAuth: grantedCount === 0,
    reachedLimit: grantedCount === 0,
  }
}

export const SaaSProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [guestToken, setGuestToken] = useState("")
  const [guestFingerprint, setGuestFingerprint] = useState("")
  const [user, setUser] = useState<SaaSUser | null>(null)
  const [usage, setUsage] = useState<UsageSummary>(DEFAULT_GUEST_USAGE)
  const TOOLS = useMemo(() => ["core-web-vitals", "url-extractor", "metadata", "url-status", "indexability"], [])
  const [usages, setUsages] = useState<Record<string, UsageSummary>>(() => {
    if (typeof window === "undefined") {
      return {}
    }
    const initial: Record<string, UsageSummary> = {}
    for (const toolId of ["core-web-vitals", "url-extractor", "metadata", "url-status", "indexability"]) {
      initial[toolId] = readGuestUsage(toolId)
    }
    return initial
  })
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>("login")
  const [authRedirectPath, setAuthRedirectPath] = useState<string | null>(null)
  const [isBootstrapping, setIsBootstrapping] = useState(true)
  const [authLoading, setAuthLoading] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const savedGuestToken =
      window.localStorage.getItem(GUEST_TOKEN_KEY) || createGuestToken()
    const savedGuestFingerprint =
      window.localStorage.getItem(GUEST_FINGERPRINT_KEY) ||
      createGuestFingerprint()
    const savedAuthToken = window.localStorage.getItem(AUTH_TOKEN_KEY)

    window.localStorage.setItem(GUEST_TOKEN_KEY, savedGuestToken)
    window.localStorage.setItem(GUEST_FINGERPRINT_KEY, savedGuestFingerprint)
    setGuestToken(savedGuestToken)
    setGuestFingerprint(savedGuestFingerprint)

    const params = new URLSearchParams(window.location.search)
    const authParam = params.get("auth") || params.get("authMode")
    if (
      authParam === "login" ||
      authParam === "signup" ||
      authParam === "forgot_password" ||
      params.get("openAuth") === "true"
    ) {
      const mode: AuthMode =
        authParam === "signup" || authParam === "forgot_password"
          ? (authParam as AuthMode)
          : "login"
      setAuthMode(mode)
      setAuthModalOpen(true)
    }

    const initialUsages: Record<string, UsageSummary> = {}
    for (const toolId of TOOLS) {
      initialUsages[toolId] = readGuestUsage(toolId)
    }
    setUsages(initialUsages)
    setUsage(initialUsages["url-extractor"] || DEFAULT_GUEST_USAGE)

    if (!savedAuthToken) {
      const usagePromises = TOOLS.map(toolId =>
        apiRequest<{ usage: UsageSummary }>("/.netlify/functions/saas-usage", {
          method: "POST",
          body: JSON.stringify({
            toolId,
            guestToken: savedGuestToken,
            guestFingerprint: savedGuestFingerprint,
          }),
        })
          .then(res => ({ toolId, usage: res.usage }))
          .catch(() => ({ toolId, usage: readGuestUsage(toolId) }))
      )
      Promise.all(usagePromises).then(results => {
        const nextUsages: Record<string, UsageSummary> = {}
        results.forEach(({ toolId, usage }) => {
          nextUsages[toolId] = usage
        })
        setUsages(nextUsages)
        if (nextUsages["url-extractor"]) {
          setUsage(nextUsages["url-extractor"])
        }
      }).finally(() => setIsBootstrapping(false))
      return
    }

    setAuthToken(savedAuthToken)

    apiRequest<{ user: SaaSUser }>("/.netlify/functions/saas-me", { method: "POST" }, savedAuthToken)
      .then(async data => {
        setUser(data.user)
        const usagePromises = TOOLS.map(toolId =>
          apiRequest<{ usage: UsageSummary }>(
            "/.netlify/functions/saas-usage",
            {
              method: "POST",
              body: JSON.stringify({ toolId }),
            },
            savedAuthToken
          ).then(res => ({ toolId, usage: res.usage }))
        )
        const results = await Promise.all(usagePromises)
        const nextUsages: Record<string, UsageSummary> = {}
        results.forEach(({ toolId, usage }) => {
          nextUsages[toolId] = usage
        })
        setUsages(nextUsages)
        if (nextUsages["url-extractor"]) {
          setUsage(nextUsages["url-extractor"])
        }
      })
      .catch(async () => {
        window.localStorage.removeItem(AUTH_TOKEN_KEY)
        setAuthToken(null)
        setUser(null)
        const usagePromises = TOOLS.map(toolId =>
          apiRequest<{ usage: UsageSummary }>("/.netlify/functions/saas-usage", {
            method: "POST",
            body: JSON.stringify({
              toolId,
              guestToken: savedGuestToken,
              guestFingerprint: savedGuestFingerprint,
            }),
          })
            .then(res => ({ toolId, usage: res.usage }))
            .catch(() => ({ toolId, usage: readGuestUsage(toolId) }))
        )
        const results = await Promise.all(usagePromises)
        const nextUsages: Record<string, UsageSummary> = {}
        results.forEach(({ toolId, usage }) => {
          nextUsages[toolId] = usage
        })
        setUsages(nextUsages)
        if (nextUsages["url-extractor"]) {
          setUsage(nextUsages["url-extractor"])
        }
      })
      .finally(() => setIsBootstrapping(false))
  }, [TOOLS])

  const openAuthModal = useCallback(
    (mode: AuthMode = "login", redirectPath?: string) => {
      if (redirectPath) {
        setAuthRedirectPath(redirectPath)
        // Also persist to localStorage so the redirect survives signup →
        // email verification → fresh login (where React state is gone)
        if (typeof window !== "undefined") {
          window.localStorage.setItem(PENDING_REDIRECT_KEY, redirectPath)
        }
      }

      setAuthMode(mode)
      setAuthModalOpen(true)
    },
    []
  )

  const closeAuthModal = useCallback(() => {
    setAuthModalOpen(false)
    setAuthRedirectPath(null)
  }, [])

  const applyAuthPayload = useCallback(
    async (payload: AuthPayload) => {
      // Use in-memory redirect first; fall back to the localStorage-persisted
      // one (set by openAuthModal) which survives signup → email-verify → login
      const stateRedirect = authRedirectPath
      const persistedRedirect =
        typeof window !== "undefined"
          ? window.localStorage.getItem(PENDING_REDIRECT_KEY)
          : null
      const redirectPath = stateRedirect || persistedRedirect

      // Clear the persisted redirect immediately so it isn't reused
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(PENDING_REDIRECT_KEY)
        window.localStorage.setItem(AUTH_TOKEN_KEY, payload.token)
      }

      setAuthToken(payload.token)
      setUser(payload.user)
      setUsage(payload.usage)

      // Analytics: Identify the user safely using only their ID
      if (payload.user?.id) {
        identifyUser(payload.user.id)
      }

      // Unblock auth UI completion immediately
      setAuthModalOpen(false)
      setAuthRedirectPath(null)

      if (redirectPath) {
        navigate(redirectPath)
      }

      // Asynchronously fetch tool usage metrics in the background without holding back UI navigation
      const usagePromises = TOOLS.map(toolId =>
        apiRequest<{ usage: UsageSummary }>(
          "/.netlify/functions/saas-usage",
          {
            method: "POST",
            body: JSON.stringify({ toolId }),
          },
          payload.token
        ).then(res => ({ toolId, usage: res.usage }))
      )
      Promise.all(usagePromises)
        .then(results => {
          const nextUsages: Record<string, UsageSummary> = {}
          results.forEach(({ toolId, usage }) => {
            nextUsages[toolId] = usage
          })
          setUsages(nextUsages)
          if (nextUsages["url-extractor"]) {
            setUsage(nextUsages["url-extractor"])
          }
        })
        .catch(err => {
          console.error("Error fetching usages after auth:", err)
        })
    },
    [authRedirectPath, TOOLS]
  )

  const login = useCallback(
    async (email: string, password: string) => {
      setAuthLoading(true)
      try {
        const payload = await apiRequest<AuthPayload>(
          "/.netlify/functions/saas-login",
          {
            method: "POST",
            body: JSON.stringify({ email, password }),
          }
        )
        await applyAuthPayload(payload)
      } finally {
        setAuthLoading(false)
      }
    },
    [applyAuthPayload]
  )

  const signup = useCallback(
    async (email: string, password: string, fullName?: string, mobile?: string) => {
      setAuthLoading(true)
      try {
        const payload = await apiRequest<any>(
          "/.netlify/functions/saas-signup",
          {
            method: "POST",
            body: JSON.stringify({ email, password, fullName, mobile }),
          }
        )
        if (payload?.success) {
          return payload
        }
        await applyAuthPayload(payload)
      } finally {
        setAuthLoading(false)
      }
    },
    [applyAuthPayload]
  )

  const loginWithGoogle = useCallback(
    async (firebaseIdToken: string) => {
      setAuthLoading(true)
      try {
        const payload = await apiRequest<AuthPayload>(
          "/.netlify/functions/saas-google-login",
          {
            method: "POST",
            body: JSON.stringify({ firebaseIdToken }),
          }
        )
        await applyAuthPayload(payload)
      } finally {
        setAuthLoading(false)
      }
    },
    [applyAuthPayload]
  )

  const resetPassword = useCallback(async (email: string) => {
    setAuthLoading(true)
    try {
      await apiRequest("/api/auth/generate-reset-link", {
        method: "POST",
        body: JSON.stringify({ email }),
      })
    } finally {
      setAuthLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(AUTH_TOKEN_KEY)
    }

    setAuthToken(null)
    setUser(null)
    resetAnalytics() // Clear PostHog session

    // Fetch guest usage for all tools
    const usagePromises = TOOLS.map(toolId =>
      apiRequest<{ usage: UsageSummary }>("/.netlify/functions/saas-usage", {
        method: "POST",
        body: JSON.stringify({ toolId, guestToken, guestFingerprint }),
      })
        .then(res => ({ toolId, usage: res.usage }))
        .catch(() => ({ toolId, usage: readGuestUsage(toolId) }))
    )
    Promise.all(usagePromises).then(results => {
      const nextUsages: Record<string, UsageSummary> = {}
      results.forEach(({ toolId, usage }) => {
        nextUsages[toolId] = usage
      })
      setUsages(nextUsages)
      if (nextUsages["url-extractor"]) {
        setUsage(nextUsages["url-extractor"])
      }
    })
  }, [guestFingerprint, guestToken, TOOLS])

  const refreshUsage = useCallback(async (toolId = "url-extractor") => {
    if (!authToken) {
      try {
        const payload = await apiRequest<{ usage: UsageSummary }>(
          "/.netlify/functions/saas-usage",
          {
            method: "POST",
            body: JSON.stringify({ toolId, guestToken, guestFingerprint }),
          }
        )

        setUsages(prev => ({ ...prev, [toolId]: payload.usage }))
        setUsage(payload.usage)
        return payload.usage
      } catch {
        const guestUsage = readGuestUsage(toolId)
        setUsages(prev => ({ ...prev, [toolId]: guestUsage }))
        setUsage(guestUsage)
        return guestUsage
      }
    }

    const payload = await apiRequest<{ usage: UsageSummary }>(
      "/.netlify/functions/saas-usage",
      {
        method: "POST",
        body: JSON.stringify({ toolId, guestToken, guestFingerprint }),
      },
      authToken
    )

    setUsages(prev => ({ ...prev, [toolId]: payload.usage }))
    setUsage(payload.usage)
    return payload.usage
  }, [authToken, guestFingerprint, guestToken])

  const consumeUsage = useCallback(
    async ({ requestedCount, toolId = "url-extractor" }: ConsumeUsagePayload) => {
      try {
        const payload = await apiRequest<ConsumeUsageResult>(
          "/.netlify/functions/saas-usage",
          {
            method: "POST",
            body: JSON.stringify({
              requestedCount,
              toolId,
              guestToken,
              guestFingerprint,
            }),
          },
          authToken
        )

        setUsages(prev => ({ ...prev, [toolId]: payload.usage }))
        setUsage(payload.usage)

        return payload
      } catch (error) {
        if (authToken) {
          throw error
        }

        const payload = consumeGuestUsageLocally(requestedCount, toolId)
        setUsages(prev => ({ ...prev, [toolId]: payload.usage }))
        setUsage(payload.usage)
        return payload
      }
    },
    [authToken, guestFingerprint, guestToken]
  )

  const startUpgrade = useCallback(async () => {
    if (!authToken) {
      openAuthModal("signup")
      return
    }

    setAuthLoading(true)
    try {
      if (typeof window === "undefined" || !(window as any).Razorpay) {
        throw new Error("Razorpay is not available right now.")
      }

      const paymentId = `razorpay_${Date.now()}`

      const payload = await apiRequest<{ user: SaaSUser; usage: UsageSummary }>(
        "/.netlify/functions/saas-upgrade",
        {
          method: "POST",
          body: JSON.stringify({ paymentId }),
        },
        authToken
      )

      setUser(payload.user)
      setUsage(payload.usage)

      // Fetch all tools' usages to reflect upgraded limits
      const usagePromises = TOOLS.map(toolId =>
        apiRequest<{ usage: UsageSummary }>(
          "/.netlify/functions/saas-usage",
          {
            method: "POST",
            body: JSON.stringify({ toolId }),
          },
          authToken
        ).then(res => ({ toolId, usage: res.usage }))
      )
      const results = await Promise.all(usagePromises)
      const nextUsages: Record<string, UsageSummary> = {}
      results.forEach(({ toolId, usage }) => {
        nextUsages[toolId] = usage
      })
      setUsages(nextUsages)
      if (nextUsages["url-extractor"]) {
        setUsage(nextUsages["url-extractor"])
      }
    } finally {
      setAuthLoading(false)
    }
  }, [authToken, openAuthModal, TOOLS])

  const requestCorporateVerificationCode = useCallback(async (email: string) => {
    if (!authToken) {
      throw new Error("You must be logged in to request a verification code.")
    }

    setAuthLoading(true)
    try {
      const payload = await apiRequest<any>(
        "/.netlify/functions/saas-verify-corporate",
        {
          method: "POST",
          body: JSON.stringify({ email }),
        },
        authToken
      )
      return payload
    } finally {
      setAuthLoading(false)
    }
  }, [authToken])

  const verifyCorporateEmailCode = useCallback(async (email: string, code: string) => {
    if (!authToken) {
      throw new Error("You must be logged in to verify your corporate email.")
    }

    setAuthLoading(true)
    try {
      const payload = await apiRequest<{ user: SaaSUser; usage: UsageSummary }>(
        "/.netlify/functions/saas-verify-corporate",
        {
          method: "POST",
          body: JSON.stringify({ email, code }),
        },
        authToken
      )

      setUser(payload.user)
      setUsage(payload.usage)

      // Fetch all tools' usages to reflect verified state
      const usagePromises = TOOLS.map(toolId =>
        apiRequest<{ usage: UsageSummary }>(
          "/.netlify/functions/saas-usage",
          {
            method: "POST",
            body: JSON.stringify({ toolId }),
          },
          authToken
        ).then(res => ({ toolId, usage: res.usage }))
      )
      const results = await Promise.all(usagePromises)
      const nextUsages: Record<string, UsageSummary> = {}
      results.forEach(({ toolId, usage }) => {
        nextUsages[toolId] = usage
      })
      setUsages(nextUsages)
      if (nextUsages["url-extractor"]) {
        setUsage(nextUsages["url-extractor"])
      }
    } finally {
      setAuthLoading(false)
    }
  }, [authToken, TOOLS])

  const value = useMemo<SaaSContextValue>(
    () => ({
      authToken,
      guestToken,
      isAuthenticated: Boolean(user && authToken),
      isBootstrapping,
      authLoading,
      user,
      usage,
      usages,
      authModalOpen,
      authMode,
      openAuthModal,
      closeAuthModal,
      login,
      signup,
      loginWithGoogle,
      resetPassword,
      logout,
      refreshUsage,
      consumeUsage,
      startUpgrade,
      requestCorporateVerificationCode,
      verifyCorporateEmailCode,
    }),
    [
      authLoading,
      authMode,
      authModalOpen,
      authToken,
      closeAuthModal,
      consumeUsage,
      guestFingerprint,
      guestToken,
      isBootstrapping,
      login,
      loginWithGoogle,
      logout,
      openAuthModal,
      refreshUsage,
      signup,
      startUpgrade,
      usage,
      usages,
      user,
      requestCorporateVerificationCode,
      verifyCorporateEmailCode,
    ]
  )

  return <SaaSContext.Provider value={value}>{children}</SaaSContext.Provider>
}

export const useSaaS = () => {
  const context = useContext(SaaSContext)
  if (!context) {
    throw new Error("useSaaS must be used within SaaSProvider")
  }
  return context
}
